import React, { useState, useEffect } from "react"
import { Button, Table, Form } from "react-bootstrap"
import { useDispatch } from "react-redux"
import _ from "lodash"

import { useSliceSelector, useSliceActions } from "@/helpers/SliceProvider"

import { useModal } from "@/helpers/useModal"

import { hasInvalidPages, getInvalidPages } from "@/components/Wizard"
import { ChatButton } from "@/components/chat"
import OperatorOnly from "@/auth_descriptor/OperatorOnly"
import { getAgreement, getForm } from "../selectors"

import SignAgreementModal from "./SignAgreementModal"
import PageStatusTable from "./PageStatusTable"

const WarningLine = ({ children }) => <i className="text-warning"> {children} </i>

const ApplicationProblems = ({ agreement }) => {
  const allAgreed = !_.some(agreement.documents, (d) => !d.agreed)

  const invalidPages = getInvalidPages()

  const anyPagesInvalid = hasInvalidPages()

  const messages = []

  if (!allAgreed) {
    messages.push(<span> Agree to all the above documents before continuing </span>)
  }

  if (anyPagesInvalid) {
    messages.push(
      <>
        <span> Address problems in previous steps </span>
        <PageStatusTable />
      </>
    )
  }

  if (messages.length == 0) return null

  return (
    <div>
      <OperatorOnly>
        <h2 className="h5"> Application Problems </h2>
        <WarningLine>
          <p>
            You must address the following application problems before you can sign the application
          </p>
        </WarningLine>
        <ol>
          {_.map(messages, (msg, idx) => (
            <li key={idx}> {msg} </li>
          ))}
        </ol>
      </OperatorOnly>
    </div>
  )
}

const DocumentAgreementTable = ({ agreements }): JSX.Element => {
  const dispatch = useDispatch()

  const { agreeToDocument } = useSliceActions()

  // TODO: Check if this is still hardcoded
  const onAccept = (documentId) => {
    dispatch(agreeToDocument({ documentId }))
  }

  return (
    <Table className="mb-2">
      <thead>
        <tr>
          <th>Agreement Document Link</th>
          <th>Acceptance</th>
          <th>Accepting User</th>
          <th>Acceptance Date</th>
        </tr>
      </thead>
      <tbody>
        {_.map(agreements, (a, index) => (
          <tr key={index}>
            <td>
              <a
                className="underline hover-underline cursor-pointer"
                href={a.url}
                target="_blank"
                rel="noreferrer"
              >
                {a.name}
              </a>
            </td>
            <td>
              <Form.Check checked={a.agreed} onChange={() => onAccept(a.id)} />
            </td>
            <td>{a.agreedByUser}</td>
            <td>{a.agreedOnDate}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  )
}

const SignatureSection = ({ agreement, allAgreed, context }) => {
  let body = <> </>

  if (context.readOnly) {
    if (agreement.signedByUser) {
      const wasInherited = Boolean(agreement.inheritedSignedByUser)
      body = (
        <div>
          <img src={agreement.signature} height={250} />
          <p className="mb-1">
            <b>{wasInherited ? "Submitted by: " : "Signed by: "}</b>
            {agreement.signedByUser} ({agreement.userCompany})
          </p>
          {wasInherited && (
            <>
              <p className="mb-1">
                <b>Signed by: </b>
                {agreement.inheritedSignedByUser} ({agreement.userCompany})
              </p>
              <p className="mb-0">
                <i>
                  Signature inherited from {agreement.inheritedSignedByUser}
                  {agreement.inheritedSignedAt &&
                    `, originally signed on ${agreement.inheritedSignedAt}`}
                  . The terms and conditions and agreement documents were unchanged since then, so a
                  new signature was not required.
                </i>
              </p>
            </>
          )}
        </div>
      )
    } else {
      body = (
        <div>
          <h3 className="h5"> Signature </h3>
          <i> Not signed yet </i>
        </div>
      )
    }
  } else if (agreement.inheritedSignature) {
    const inheritedSigner = agreement.inheritedSignedByUser || "another user at your company"
    body = (
      <div>
        <h3 className="h5"> Signature </h3>
        <i>
          The terms and conditions and agreement documents are unchanged since {inheritedSigner}{" "}
          signed them
          {agreement.inheritedSignedAt && ` on ${agreement.inheritedSignedAt}`}, so no new signature
          is required. You can submit your updates using their signature, or sign again before
          submitting.
        </i>
        <div>
          <img src={agreement.inheritedSignature} height={150} />
          <p className="mb-0">
            <b>Inherited signature of: </b>
            {inheritedSigner}
            {agreement.inheritedSignedAt && ` (${agreement.inheritedSignedAt})`}
          </p>
        </div>
      </div>
    )
  } else if (!allAgreed) {
    body = null
  }

  return (
    <div>
      <div className="mb-2">{body}</div>
    </div>
  )
}

const AgreementForm = (props): JSX.Element => {
  const dispatch = useDispatch()
  const { chats } = useSliceSelector(getForm)

  const { submitAgreement, agreeToAllDocuments } = useSliceActions()

  const { agreement, allAgreed } = useSliceSelector((state) => {
    const agreement = getAgreement(state)
    const allAgreed = !_.some(agreement.documents, (d) => !d.agreed)

    return { agreement, allAgreed }
  })

  const anyPagesInvalid = hasInvalidPages()

  const [canSignAgreement, setCanSignAgreement] = useState(true)

  const onSubmitSignature = (data) => {
    dispatch(submitAgreement(data))
  }

  // Agreement and documents are unchanged, so no new signature is required:
  // keep the existing acceptance and submit the application updates.
  const onSubmitUpdates = () => {
    dispatch(agreeToAllDocuments())
    dispatch(submitAgreement({ signature: agreement.inheritedSignature }))
  }

  useEffect(() => {
    setCanSignAgreement(allAgreed && !anyPagesInvalid)
  }, [anyPagesInvalid, allAgreed])

  const toggleAgreementModal = useModal((props) => (
    <SignAgreementModal {...props} onSubmit={onSubmitSignature} />
  ))

  const chat = chats.find((chat) => chat.section === "confirm_agreement")

  return (
    <>
      <div style={{ display: "flex", alignItems: "center" }}>
        <h2 className="h5">{props.title}</h2>
        {chat && (
          <div
            style={{
              whiteSpace: "normal",
              overflowWrap: "break-word",
              wordBreak: "break-word",
            }}
            key={chat.id}
            className="mt-n2 ms-2"
          >
            <ChatButton chat={chat} />
          </div>
        )}
      </div>
      <div dangerouslySetInnerHTML={{ __html: agreement.agreementText }} />

      <fieldset disabled={props.context.readOnly}>
        <DocumentAgreementTable agreements={agreement.documents} />
      </fieldset>

      <SignatureSection context={props.context} allAgreed={allAgreed} agreement={agreement} />

      <ApplicationProblems agreement={agreement} />

      <div className="d-flex gap-2">
        {props.formNavigation}
        {!props.context.readOnly && agreement.inheritedSignature && (
          <Button variant="success" onClick={onSubmitUpdates} disabled={anyPagesInvalid}>
            Submit Application Updates
          </Button>
        )}
        {!props.context.readOnly && (
          <Button
            variant={agreement.inheritedSignature ? "outline-success" : "success"}
            onClick={toggleAgreementModal}
            disabled={!canSignAgreement}
          >
            {agreement.inheritedSignature ? "Sign Again" : "Sign and Continue"}
          </Button>
        )}
      </div>
    </>
  )
}

export default AgreementForm
